import { ReactElement, useMemo } from "react";
import styled from "styled-components";

interface InscriptionProgressBarProps {
  /** Number of steps in the registration spine (one segment each). */
  total: number;
  /** 0-based index of the current step within that spine (-1 = not on a spine step). */
  current: number;
  /** Show the small "2 / 4" counter above the bar (mirrors the questionnaire's %). */
  showCounter?: boolean;
}

/**
 * Multi-step progress bar shown below the header during the registration journey.
 *
 * Reads like the questionnaire's single-bar <Progress> (same emphasis gradient fill
 * on a translucent track), but split into N mini segments — one per registration
 * step. Every step reached so far (including the current one) is filled; upcoming
 * steps stay on the empty track, so the bar visibly advances a segment per screen.
 *
 * Renders nothing off the spine (current < 0) or when there is only a single step
 * (nothing meaningful to segment).
 */
const InscriptionProgressBar = ({
  total,
  current,
  showCounter = true,
}: InscriptionProgressBarProps): ReactElement | null => {
  const segments = useMemo(
    () => Array.from({ length: Math.max(total, 0) }, (_, i) => i),
    [total]
  );

  if (total < 2 || current < 0) return null;

  return (
    <ProgressContainer
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={total}
      aria-valuenow={current + 1}
    >
      {showCounter && (
        <ProgressLabel>
          {current + 1} / {total}
        </ProgressLabel>
      )}

      <SegmentTrack>
        {segments.map((i) => (
          <Segment key={i} $active={i === current}>
            <SegmentFill $filled={i <= current} />
          </Segment>
        ))}
      </SegmentTrack>
    </ProgressContainer>
  );
};

const ProgressContainer = styled.div`
  /* Matches the questionnaire <Progress> padding so it sits the same distance from
     the header/edges. */
  width: 100%;
  padding: 10px 16px 4px;
`;

const ProgressLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  text-align: right;
  margin-bottom: 6px;
  /* Muted ink so the count reads as a hint, not a headline. */
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.55);
`;

const SegmentTrack = styled.div`
  display: flex;
  gap: 6px;
  width: 100%;
`;

/** One mini bar. The active (current) one gets a soft emphasis ring so "you are here" reads. */
const Segment = styled.div<{ $active?: boolean }>`
  flex: 1 1 0;
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  /* Same translucent track as the questionnaire ProgressBarWrapper. */
  background-color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.08);
  box-shadow: ${(props) =>
    props.$active
      ? "0 0 0 2px rgba(var(--app-color-emphasis-rgb, 45, 182, 180), 0.18)"
      : "none"};
  transition: box-shadow 0.3s ease-in-out;
`;

const SegmentFill = styled.div<{ $filled?: boolean }>`
  height: 100%;
  border-radius: 999px;
  transform-origin: left center;
  /* scaleX (not width) so the fill sweeps in smoothly and stays GPU-cheap. */
  transform: scaleX(${(props) => (props.$filled ? 1 : 0)});
  transition: transform 0.4s ease-in-out;
  /* The questionnaire's signature fill: strong emphasis tone sweeping to its pale
     -soft end (falls back to primary/tint, then the historical hexes). */
  background: linear-gradient(
    270deg,
    var(--app-color-emphasis, var(--app-color-primary, #2db6b4)) 0%,
    var(--app-color-emphasis-soft, var(--app-color-primary-tint, #abf0ef)) 105%
  );
`;

export default InscriptionProgressBar;
